import { authHeader } from "./auth-header";
import apiClient from "./axios";
import axios from "axios";
import swal from "sweetalert";
import { menuCache } from "@/utils/menuCache";

// Base API URL
const API_URL = process.env.VUE_APP_API_BASE_URL;

export const userService = {
  API_URL,

  // Existing auth
  login,
  logout,
  register,
  getAll,
  fetchRoles,

  // 🔐 Client self-activation flow
  verifyClient,
  activateClient,
  setPassword,
};

/* =========================================================
   AUTHENTICATION
   ========================================================= */

function login(email, password) {
  return axios
    .post(API_URL + "login_request", { email, password })
    .then((response) => {
      if (response.status === 200) {
        menuCache.clear(); // new token = new permissions
        swal({
          icon: "success",
          text: "Welcome",
          timer: 1500,
          buttons: false,
        });

        const user = { token: response.data };
        localStorage.setItem("user", JSON.stringify(user));
        return user;
      } else {
        swal({
          icon: "error",
          text: "Login failed: Invalid credentials.",
          timer: 1500,
          buttons: false,
        });
        return Promise.reject("Invalid login");
      }
    })
    .catch((error) => {
      swal({
        icon: "error",
        text: "Server error: " + (error.message || "Unknown error"),
        timer: 2000,
        buttons: false,
      });
      return Promise.reject(error);
    });
}

function logout() {
  menuCache.clear();
  localStorage.removeItem("user");
}

async function register(user) {
  try {
    const response = await axios.post(API_URL + "post_service", user);
    if (response.status === 200) {
      swal({
        icon: "success",
        text: "Success",
        timer: 1500,
        buttons: false,
      });
    }
  } catch (error) {
    swal({
      icon: "error",
      text: error.message || "Registration failed",
      timer: 1500,
      buttons: false,
    });
  }
}

function getAll() {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };
  return fetch(`/users`, requestOptions).then(handleResponse);
}

/* =========================================================
   CLIENT SELF-ACTIVATION FLOW
   ========================================================= */

/**
 * STEP 1: Verify client identity
 * POST /client/verify
 */
function verifyClient(payload) {
  return axios
    .post(API_URL + "client/verify", payload)
    .then((response) => {
      return response.data; // { fullName }
    })
    .catch((error) => {
      swal({
        icon: "error",
        text:
          error.response?.data?.message ||
          "Verification failed. Please check your details.",
        timer: 2000,
        buttons: false,
      });
      return Promise.reject(error);
    });
}

/**
 * STEP 2: Activate client (creates inactive user + sends email)
 * POST /client/activate
 */
function activateClient(payload) {
  return axios
    .post(API_URL + "client/activate", payload)
    .then(() => {
      swal({
        icon: "success",
        text: "Activation email sent. Please check your inbox.",
        timer: 2000,
        buttons: false,
      });
    })
    .catch((error) => {
      swal({
        icon: "error",
        text:
          error.response?.data?.message ||
          "Activation failed. Please try again.",
        timer: 2000,
        buttons: false,
      });
      return Promise.reject(error);
    });
}

/**
 * STEP 3: Set password from email activation link
 * POST /auth/set-password
 */
function setPassword(payload) {
  // payload = { token, password }
  return axios
    .post(`${API_URL}auth/set-password/${payload.token}`, { password: payload.password })
    .then(() => {
      swal({
        icon: "success",
        text: "Account activated successfully. Please login.",
        timer: 2000,
        buttons: false,
      });
    })
    .catch((error) => {
      swal({
        icon: "error",
        text:
          error.response?.data?.message ||
          "Invalid or expired activation link.",
        timer: 2000,
        buttons: false,
      });
      return Promise.reject(error);
    });
}


/* =========================================================
   SHARED RESPONSE HANDLER
   ========================================================= */

function handleResponse(response) {
  return response.text().then((text) => {
    const data = text && JSON.parse(text);
    if (!response.ok) {
      if (response.status === 401) {
        logout();
        location.reload(true);
      }
      const error = (data && data.message) || response.statusText;
      return Promise.reject(error);
    }
    return data;
  });
}

/* =========================================================
   FETCH CURRENT USER ROLES
   ========================================================= */

async function fetchRoles() {
  try {
    const response = await apiClient.get("current_user_roles");
    // response.data should be array of roles like ["CLIENT", "ADMIN"]
    return response.data || [];
  } catch (error) {
    console.error("Failed to fetch roles:", error.message);
    swal({
      icon: "error",
      text: "Failed to fetch user roles",
      timer: 2000,
      buttons: false,
    });
    return [];
  }
}

